import { CxpTask, CxpTaskIntegration, CxpTaskStatus } from '../../models';
import {
  CxpDeliverableTaskApiResponse,
  CxpDeliverableTaskWithClient,
  SafeSendSigner,
} from '../cxp-deliverable-api.service';

/**
 * Derives a deliverable's task status from the user's most recent VIEWED
 * event vs the deliverable's last update.
 *
 * Rules (per product):
 *   - lastViewedAt missing → 'open' (never viewed)
 *   - lastViewedAt < updatedAt → 'open' (deliverable changed since last view)
 *   - lastViewedAt >= updatedAt → 'completed'
 *   - updatedAt missing (defensive) → 'open'
 */
function deriveDeliverableStatus(
  updatedAt: string | null,
  lastViewedAt: string | null,
): CxpTaskStatus {
  if (!lastViewedAt || !updatedAt) return 'open';
  return new Date(lastViewedAt).getTime() >= new Date(updatedAt).getTime()
    ? 'completed'
    : 'open';
}

/**
 * Reads the most recent `viewedAt` timestamp across the deliverable's
 * files (from `vw_deliverable_file`, scoped to the contact by Hasura
 * permissions). Returns `null` when no file has ever been viewed.
 *
 * Caveat: the underlying `deliverable_file` row carries a single timestamp
 * per (deliverable, file). For multi-contact deliverables that may
 * conflate different contacts' views — confirm with backend if isolation
 * matters here.
 */
function extractLastViewedAt(model: CxpDeliverableTaskApiResponse): string | null {
  const timestamps = model.deliverableFiles
    .map((f) => f.viewedAt)
    .filter((v): v is string => v !== null && v !== undefined);
  if (timestamps.length === 0) return null;
  let latest = timestamps[0];
  for (const ts of timestamps) {
    if (Date.parse(ts) > Date.parse(latest)) latest = ts;
  }
  return latest;
}

function filterSignersByAuthEmail(
  signers: SafeSendSigner[],
  authEmails: string[],
): SafeSendSigner[] {
  // No auth email = no way to identify which signer is "you" → no rows.
  // Showing nothing is correct: returning the rows blindly would point the
  // user at someone else's deepLink. See PR #1532 review thread.
  if (authEmails.length === 0) return [];
  const allowed = new Set(authEmails.map((e) => e.toLowerCase()));
  return signers.filter((s) => s.email && allowed.has(s.email.toLowerCase()));
}

/**
 * Returns the lowercased emails that appear 2+ times in the matching set —
 * those are the rows that need a signer-name suffix to be distinguishable.
 */
function collectSharedEmails(matching: SafeSendSigner[]): Set<string> {
  const counts = new Map<string, number>();
  for (const s of matching) {
    const key = s.email.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const shared = new Set<string>();
  for (const [email, count] of counts) {
    if (count > 1) shared.add(email);
  }
  return shared;
}

function buildSignerTask(
  model: CxpDeliverableTaskWithClient,
  signer: SafeSendSigner,
  disambiguate: boolean,
): CxpTask {
  const updatedAt = model.updatedAt ?? null;
  const integration: CxpTaskIntegration = {
    type: 'safesend',
    url: signer.deepLink,
    signerGuid: signer.signerGuid,
    signerName: signer.name,
    signerLabel: signer.signerTypeLabel,
    isSigned: signer.isSigned,
  };

  return {
    key: `external-deliverable-${model.id}-${signer.signerGuid}`,
    id: model.id,
    type: 'external-deliverable',
    sourceId: model.id,
    // Suffix the signer name only when 2+ matching signers would otherwise
    // collide on the same row label — replaces the shared-email dropdown.
    name: disambiguate ? `${model.name} - ${signer.name}` : model.name,
    description: model.description ?? '',
    createdAt: model.createdAt ?? null,
    dueDate: null,
    updatedAt,
    lastViewedAt: extractLastViewedAt(model),
    // Per-signer status: signed signers move to Completed; unsigned stay Open.
    status: signer.isSigned ? 'completed' : 'open',
    sourceStatus: model.status,
    actionRequired: false,
    progress: null,
    staffIds: [],
    clientId: model.clientId,
    jobId: model.jobId,
    integration,
    serviceLine: null,
    clientName: null,
    projectName: model.project?.name ?? null,
  };
}

/**
 * Legacy fallback for pre-remake SafeSend deliverables that only carry the
 * single shared `safesend_deepLink`. Preserves today's behavior so the
 * rollout is non-disruptive — once `safesend_signers` is populated by the
 * backend the per-signer branch takes over.
 */
function buildLegacyExternalTask(
  model: CxpDeliverableTaskWithClient,
  url: string,
): CxpTask {
  const updatedAt = model.updatedAt ?? null;
  const lastViewedAt = extractLastViewedAt(model);
  return {
    key: `external-deliverable-${model.id}`,
    id: model.id,
    type: 'external-deliverable',
    sourceId: model.id,
    name: model.name,
    description: model.description ?? '',
    createdAt: model.createdAt ?? null,
    dueDate: null,
    updatedAt,
    lastViewedAt,
    status: deriveDeliverableStatus(updatedAt, lastViewedAt),
    sourceStatus: model.status,
    actionRequired: false,
    progress: null,
    staffIds: [],
    clientId: model.clientId,
    jobId: model.jobId,
    integration: { type: 'safesend', url },
    serviceLine: null,
    clientName: null,
    projectName: model.project?.name ?? null,
  };
}

function buildRegularDeliverableTask(
  model: CxpDeliverableTaskWithClient,
): CxpTask {
  const updatedAt = model.updatedAt ?? null;
  const lastViewedAt = extractLastViewedAt(model);
  return {
    key: `deliverable-${model.id}`,
    id: model.id,
    type: 'deliverable',
    sourceId: model.id,
    name: model.name,
    description: model.description ?? '',
    createdAt: model.createdAt ?? null,
    dueDate: null,
    updatedAt,
    lastViewedAt,
    status: deriveDeliverableStatus(updatedAt, lastViewedAt),
    sourceStatus: model.status,
    actionRequired: false,
    progress: null,
    staffIds: [],
    clientId: model.clientId,
    jobId: model.jobId,
    integration: null,
    serviceLine: null,
    clientName: null,
    projectName: model.project?.name ?? null,
  };
}

/**
 * Maps a deliverable to one or more task rows.
 *
 *   - Deliverable with no `clientId`: dropped — a deliverable not attached
 *     to a client has no useful row in the tasks list.
 *   - SafeSend with `safesend_signers`: one row per signer whose email
 *     matches `authEmails` (case-insensitive). Zero matches → no rows.
 *   - SafeSend legacy (only `safesend_deepLink`): single row, today's
 *     behavior preserved.
 *   - Regular deliverable: single row.
 */
export function mapDeliverableToTask(
  model: CxpDeliverableTaskApiResponse,
  authEmails: string[],
): CxpTask[] {
  // Defense in depth — `getDeliverablesForTasks` already drops null-clientId
  // rows, but the mapper is exported and could be called directly with a raw
  // API row, so guard here too.
  if (model.clientId == null) return [];
  const withClient = model as CxpDeliverableTaskWithClient;

  const customFields = model.custom_field_values;
  const signers = customFields?.safesend_signers;

  if (signers && signers.length > 0) {
    const matching = filterSignersByAuthEmail(signers, authEmails);
    // Two signers may share an email (taxpayer + spouse on a joint return).
    // When that happens AND both match the auth user, the rows would render
    // identical labels — suffix the signer name so the user can tell them apart.
    const sharedEmails = collectSharedEmails(matching);
    return matching.map((signer) =>
      buildSignerTask(withClient, signer, sharedEmails.has(signer.email.toLowerCase())),
    );
  }

  if (customFields?.safesend_documentId && customFields?.safesend_deepLink) {
    return [buildLegacyExternalTask(withClient, customFields.safesend_deepLink)];
  }

  return [buildRegularDeliverableTask(withClient)];
}
